import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { uploadPrescription } from '../modal-forms/upload-files-lsq/helper';

// "Upload Photo of the Open Box" — proof-of-packing, taken before the box is sealed.
// OPTIONAL as of 2026-09-23: a delivery can be created without one.
// New in the logistics dashboard (`.scratch/logistics-dashboard/IMPLEMENTATION.md` §11);
// every other field in the Create Delivery modal is unchanged.
//
// Reuses POST /file/upload/prescription, which already validates image types, pushes to
// Azure and returns `{ image, url, originalFilename }`. `image` is the blob NAME — that is
// what gets stored in deliveries.deliveryBoxImages. The `url` alongside it is SAS-signed
// and expires, so it is used for the on-screen preview only and never persisted.
//
// The endpoint is named for prescriptions but is a generic image/PDF uploader; a
// delivery-specific route would be the same code under a different path.
const OpenBoxPhotoField = ({ images, previews, onChange }) => {
    const [uploading, setUploading] = useState(false);

    const handleFiles = async (event) => {
        const files = Array.from(event.target.files || []);
        // Let the same file be picked again after a remove — without this the input keeps
        // the old value and onChange never fires.
        event.target.value = '';
        if (files.length === 0) return;

        setUploading(true);
        try {
            const uploaded = await uploadPrescription(files);
            const entries = (uploaded || []).filter((entry) => entry?.image);

            if (entries.length === 0) {
                toast.error("Couldn't upload photo");
                return;
            }
            // Partial success is possible — say so rather than implying everything landed.
            if (entries.length < files.length) {
                toast.warn(`${entries.length} of ${files.length} photos uploaded`);
            }

            onChange(
                [...images, ...entries.map((entry) => entry.image)],
                [...previews, ...entries.map((entry) => entry.url)]
            );
        } catch (error) {
            toast.error("Couldn't upload photo");
        } finally {
            setUploading(false);
        }
    };

    const removeAt = (index) => {
        onChange(
            images.filter((_, i) => i !== index),
            previews.filter((_, i) => i !== index)
        );
    };

    return (
        <div className="flex flex-col gap-[0.5rem]">
            <div className="flex justify-between items-center">
                <span>Upload Photo of the Open Box</span>
                <input
                    type="file"
                    accept="image/*"
                    multiple
                    disabled={uploading}
                    onChange={handleFiles}
                    className="text-[1.1rem]"
                />
            </div>

            {uploading && <span className="text-sm text-gray-500">Uploading...</span>}

            {images.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {images.map((blobName, index) => (
                        <div
                            key={blobName}
                            className="relative border border-gray-200 rounded-lg overflow-hidden"
                        >
                            {previews[index] ? (
                                <img
                                    src={previews[index]}
                                    alt={`Open box ${index + 1}`}
                                    className="h-[70px] w-[70px] object-cover"
                                />
                            ) : (
                                <div className="h-[70px] w-[70px] flex items-center justify-center text-xs text-gray-500 px-1 text-center">
                                    Uploaded
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => removeAt(index)}
                                title="Remove"
                                className="absolute top-0 right-0 bg-red-500 text-white-50 w-[18px] h-[18px] leading-[18px] text-center text-xs"
                            >
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

OpenBoxPhotoField.propTypes = {
    images: PropTypes.arrayOf(PropTypes.string).isRequired,
    previews: PropTypes.arrayOf(PropTypes.string).isRequired,
    onChange: PropTypes.func.isRequired,
};

export default OpenBoxPhotoField;
